/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description practice location utils
 */
import _ from 'lodash';
import { LOCATION_STATUS } from 'app-constants/admin/location';
import { addressFieldKeys } from 'components/forms/schema/locationFormSchema';
import { createAddress } from 'utils/prepareFhirDataType';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';

/**
 * filter empty locations
 */
const getValidLocations = (locations: string[]): string[] => {
	return _.compact(locations);
};

/**
 * attributes for add & edit Provider model
 */
const getLocationAttributes = (): Location.IFormModel => {
	return {
		name: '',
		status: LOCATION_STATUS.active.value,
		[addressFieldKeys.line.key]: addressFieldKeys.line.value,
		[addressFieldKeys.city.key]: addressFieldKeys.city.value,
		[addressFieldKeys.state.key]: addressFieldKeys.state.value,
		[addressFieldKeys.postalCode.key]: addressFieldKeys.postalCode.value,
		[addressFieldKeys.country.key]: addressFieldKeys.country.value,
		[addressFieldKeys.phone.key]: addressFieldKeys.phone.value,
	};
};

/**
 * Add Location Info form model
 */
const prepareLocationModel = (location?: Location.IFormModel): Location.IFormModel => {
	if (!location) {
		return getLocationAttributes();
	}
	return _.mapValues(getLocationAttributes(), (defaultValue, key) => {
		const value = _.get(location, key);
		return _.isUndefined(value) ? defaultValue : value;
	});
};

const prepareLocationFhirModel = (model: Location.IFormModel, locationId?: string): Fhir.Location => {
	const fhirResource: Fhir.Location = {
		name: model.name,
		resourceType: 'Location',
		status: model.status ? LOCATION_STATUS.active.fhirValue : LOCATION_STATUS.inactive.fhirValue,
	};
	const address = createAddress({
		city: model.city,
		state: model.state,
		line: [model.line],
		postalCode: model.postalCode,
		country: model.country,
	});

	if (locationId) {
		fhirResource.id = locationId;
	}
	if (address) {
		fhirResource.address = address;
	}
	if (model.phone) {
		fhirResource.contact = [{ telecom: [{ system: fhirSystemMapping.Phone as Fhir.IContactSystem, value: model.phone }] }];
	}
	return fhirResource;
};

const prepareLocationPostModel = (model: Location.IFormModel): Location.FhirAddModel => {
	return {
		fhirResource: prepareLocationFhirModel(model),
		providers: [],
	};
};

const prepareLocationUpdateModel = (locationId: string, model: Location.IFormModel): Location.FhirAddModel => {
	return {
		fhirResource: prepareLocationFhirModel(model, locationId),
		providers: [],
	};
};

const getParsedLocationDetails = (model: Location.IFhirModel): Location.IFormModel => {
	const fhirResource = model?.fhirResource as Fhir.Location;
	return {
		name: fhirResource?.name,
		status: !!_.find(LOCATION_STATUS, { fhirValue: fhirResource?.status })?.value,
		line: fhirResource?.address?.line?.[0],
		city: fhirResource?.address?.city,
		state: fhirResource?.address?.state,
		postalCode: fhirResource?.address?.postalCode,
		country: fhirResource?.address?.country,
		phone: fhirResource.contact?.[0]?.telecom?.[0]?.value,
	};
};

const getAddressString = (location: Location.IModel): string => {
	const addressParts = [location?.line, location?.city, location?.state, location?.postalCode, location?.country];
	return _.join(_.compact(addressParts), ', ');
};

export {
	getValidLocations,
	prepareLocationModel,
	prepareLocationFhirModel,
	prepareLocationPostModel,
	prepareLocationUpdateModel,
	getParsedLocationDetails,
	getAddressString,
};
